<template lang="pug">
  v-card
    v-container
      outlook-connect-modal(
        ref="outlookLoginModalFromAccount"
        @new-account-connected="handleSuccesfullConnection"
        :featureIds="allFeatureIds"
      )
      imap-connect-modal(
        ref="imapLoginModalFromAccount"
        @new-account-connected="handleSuccesfullConnection"
        :featureIds="allFeatureIds"
      )
      v-layout(row wrap)
        v-flex(xs8)
          div.mr-2.icon-container(:class="getIconContainerClass()")
            v-icon.icon {{getProviderIcon()}}
          div.title.pt-2 {{emailAccount.email}}
        v-flex
          v-layout(justify-end)
            v-btn(
              v-if="!emailAccount.needs_reauthentication && !(emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z')"
              @click="disconnectAccount(emailAccount.id, emailAccount.email_account_type_id)"
              color="error"
            ) Disconnect Email
      v-layout(
        v-if="(emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z' && emailAccount.email_account_type_id === emailAccountTypes.exchange.id)"
        column 
        align-center
      )
        v-flex
          h2.orange--text.text--darken-2 Reconnect your email to LionDesk
        v-flex
          div Microsoft has updated its security defaults to ensure user accounts and the data they contain are far better protected going forward.
        v-flex
          div To continue with connecting your email, select Reconnect with Microsoft below.
        v-flex
          div If you want to discontinue syncing completely, select Remove Email.
        v-layout(row)
          v-flex(xs8)
            v-btn.title-case(
              @click="turnOnFeature(0, true, true)"
              color="primary"
            ) Reconnect with Microsoft
          v-flex(xs6)
            v-btn.title-case(
              @click="disconnectAccount(emailAccount.id, emailAccount.email_account_type_id)"
              color="error"
            ) Remove Email
      v-layout(
        v-else-if="emailAccount.needs_reauthentication"
        column 
        align-center
      )
        v-flex
          h2.orange--text.text--darken-2 Reconnect your email to LionDesk
        v-flex
          div It seems like we don't have access to your third party email anymore. Reconnect now to continue syncing your information for this email.
        v-flex
          div If you want to stop the sync completely, you can remove this email.
        v-layout(row)
          v-flex(xs6)
            v-btn.title-case(
              @click="turnOnFeature(0, true)"
              color="primary"
            ) Reconnect Email
          v-flex(xs6)
            v-btn.title-case(
              @click="disconnectAccount(emailAccount.id, emailAccount.email_account_type_id)"
              color="error"
            ) Remove Email
    v-divider
    div.service-header.px-4.py-2
      v-layout(row wrap)
        v-flex(xs10)
          span Services
        v-flex(xs2)
          span Status
    v-divider
    div.service-row.px-4.py-2(
      v-for="service in providerServices"
    )
      v-layout(row wrap)
        v-flex(xs9)
          v-icon {{service.icon}}
          span.pl-2 {{service.description}}
        v-flex(v-if="hasFeatureActivated(service.featureId)" xs1)
          v-layout(align-center)
            v-flex.text-xs-right.pr-3
              v-icon(
                v-if="service.featureId === features.contactSync.id"
                @click="openSyncContactConfiguration()"
                small
                :disabled="emailAccount.needs_reauthentication || (emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z')"
              ) settings
        v-flex(v-if="hasFeatureActivated(service.featureId)" xs2)
          v-icon(
            small
            :color="emailAccount.needs_reauthentication || (emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z') ? 'grey': 'success'"
          ) fiber_manual_record
          span Active
        v-flex(v-else)
          v-chip.grant-access-button(
            v-if="hasAccessToGrantAccess(emailAccount.email_account_type_id, service.featureId) && !emailAccount.needs_reauthentication && !(emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z')"
            @click="turnOnFeature(service.featureId)"
            label
            color="primary"
          )
            | Grant Access
          v-chip.grant-access-button(
            v-else
            label
          )
            | Grant Access
</template>

<script>
import emailAccountTypes from './config/email-account-types';
import SERVICES from './config/services';
import FEATURES from './config/features';
import EMAIL_ACCOUNT_TYPES from './config/email-account-types';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import OutlookConnectModal from './components/Connect/outlook/ConnectEmailAccountModal';
import ImapConnectModal from './components/Connect/imap/ImapConnectModal';
import microsoftOauth from './config/microsoft-oauth';

export default {
  name: 'EmailAccountCard',
  components: {
    OutlookConnectModal,
    ImapConnectModal,
  },
  created() {
    this.providerServices = this.getProviderServices(this.emailAccount.email_account_type_id);
  },
  data() {
    return {
      services: SERVICES,
      features: FEATURES,
      emailAccountTypes: EMAIL_ACCOUNT_TYPES,
      providerServices: [],
      allFeatureIds: [],
    };
  },
  props: {
    emailAccount: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      userHasFeatureFlag: 'userInformation/userHasFeatureFlag',
    }),
    ...mapState({
      emailFeatures: state => state.emailManagement.features,
    }),
  },
  methods: {
    ...mapActions('emailManagement', [
      'initGmailConnection',
      'connectGmailToNylas',
      'loadEmailAccounts',
      'disconnectEmailAccount',
      'disconnectOutlook'
    ]),
    ...mapMutations('emailManagement', ['setOutlookEmailSelected', 'setIMAPEmailSelected']),
    getScopesByFeatureAndProvider(featureIds, providerId) {
      const features = this.emailFeatures.filter(
        feature =>
          featureIds.includes(feature.feature_id) && feature.email_account_type_id === providerId,
      );
      let scopes = features.map(feature => feature.provider_scopes.join(' ')).join(' ');

      // new graph api scopes for Microsoft
      scopes = scopes.replace('https://outlook.office365.com/EAS.AccessAsUser.All https://outlook.office365.com/EWS.AccessAsUser.All', 'Mail.ReadWrite')
        .replace('https://outlook.office365.com/calendars.readwrite', 'Calendars.ReadWrite')
        .replace('https://outlook.office365.com/contacts.readwrite', 'Contacts.ReadWrite')
        .replace('https://outlook.office365.com/mail.send', 'Mail.Send');

      return scopes;
    },
    hasFeatureActivated(featureId) {
      return this.emailAccount.feature_ids.some(id => id === featureId);
    },
    async turnOnFeature(featureId, isReauth, isMicrosoftReauth) {
      switch (this.emailAccount.email_account_type_id) {
        case emailAccountTypes.gmail.id:
          const googleFeatureIds = isReauth ? this.emailAccount.feature_ids : [featureId];
          const gmailScopes = this.getScopesByFeatureAndProvider(
            googleFeatureIds,
            emailAccountTypes.gmail.id,
          );
          await this.handleLoginGmail(featureId, gmailScopes);
          break;
        case emailAccountTypes.outlook.id:
          if (!isMicrosoftReauth) {
            this.setOutlookEmailSelected(this.emailAccount.email);
            await this.handleLoginOutlook(featureId, isReauth);
            break;
          }
          // Outlook to Microsoft nylas change
          const outlookFeatureIds = this.emailAccount.feature_ids;
          const outlookScopes = this.getScopesByFeatureAndProvider(
            outlookFeatureIds,
            emailAccountTypes.exchange.id,
          );
          this.handleLoginExchange(outlookFeatureIds, outlookScopes, isMicrosoftReauth);
          break;
        case emailAccountTypes.exchange.id:
          const exchangeFeatureIds = isReauth
            ? this.emailAccount.feature_ids
            : [featureId, ...this.emailAccount.feature_ids];
          const exchangeScopes = this.getScopesByFeatureAndProvider(
            exchangeFeatureIds,
            emailAccountTypes.exchange.id,
          );
          this.handleLoginExchange(exchangeFeatureIds, exchangeScopes);
          break;
        case emailAccountTypes.imap.id:
          this.setIMAPEmailSelected(this.emailAccount.email);
          await this.handleLoginImap(featureId, isReauth);
          break;
        default:
          return;
      }
    },
    handleLoginOutlook(featureId, isReauth) {
      this.allFeatureIds = isReauth
        ? this.emailAccount.feature_ids
        : [featureId, ...this.emailAccount.feature_ids];
      this.$refs.outlookLoginModalFromAccount.modal_open();
    },
    handleLoginImap(featureId, isReauth) {
      this.allFeatureIds = isReauth
        ? this.emailAccount.feature_ids
        : [featureId, ...this.emailAccount.feature_ids];
      this.$refs.imapLoginModalFromAccount.modal_open();
    },
    async handleSuccesfullConnection() {
      await this.loadEmailAccounts(true);
    },
    async handleLoginGmail(featureId, scopes) {
      localStorage.setItem('googleFeatureIds', JSON.stringify([featureId]));
      localStorage.setItem('googleScopesToBeGranted', JSON.stringify(scopes));

      const oauthURL = await this.initGmailConnection(scopes);
      const authURL = `${oauthURL}&login_hint=${this.emailAccount.email}`;
      const signInPopup = window.open(authURL, 'Google Login', 'width=400,height=600');
      const timer = setInterval(() => {
        if (signInPopup.closed) {
          clearInterval(timer);
          const emailConnected = localStorage.getItem('googleEmailConnected');
          localStorage.removeItem('googleEmailConnected');

          if (emailConnected) {
            this.loadEmailAccounts(true);
          }
        }
      }, 500);
    },
    handleLoginExchange(featureIds, scopes, isMicrosoftReauth) {
      localStorage.setItem('exchangeScopes', scopes);
      localStorage.setItem('exchangeFeatureIds', JSON.stringify(featureIds));

      const authURL = `${microsoftOauth.AUTH_URL}${scopes}&login_hint=${this.emailAccount.email}`;
      const signInPopup = window.open(authURL, 'Microsoft Login', 'width=400,height=600');
      const timer = setInterval(async () => {
        if (signInPopup.closed) {
          clearInterval(timer);
          const emailConnected = localStorage.getItem('exchangeEmailConnected');
          localStorage.removeItem('exchangeEmailConnected');

          if (emailConnected) {
            if (isMicrosoftReauth) {
              await this.disconnectOutlook(this.emailAccount.id);
            }
            this.loadEmailAccounts(true);
          }
          this.$emit('reauthComplete');
        }
      }, 500);
    },
    getIconContainerClass() {
      switch (this.emailAccount.email_account_type_id) {
        case emailAccountTypes.gmail.id:
          return 'provider-gmail';
        case emailAccountTypes.outlook.id:
          return 'provider-outlook';
        case emailAccountTypes.exchange.id:
          return 'provider-exchange';
        case emailAccountTypes.imap.id:
          return 'provider-imap';
        default:
          return 'provider-default';
      }
    },
    getProviderIcon() {
      switch (this.emailAccount.email_account_type_id) {
        case emailAccountTypes.gmail.id:
          return '$vuetify.icons.gmail';
        case emailAccountTypes.outlook.id:
          return '$vuetify.icons.outlook_outline';
        case emailAccountTypes.exchange.id:
          return '$vuetify.icons.microsoft';
        case emailAccountTypes.imap.id:
          return '$vuetify.icons.other_mail';
        default:
          return 'email';
      }
    },
    disconnectAccount(emailAccountId, providerId) {
      this.$emit('disconnect-account', {
        id: emailAccountId,
        providerId: providerId,
      });
    },
    getProviderServices(providerId) {
      const providerServices = this.services.find(service => service.providerId === providerId);
      return providerServices.features;
    },
    openSyncContactConfiguration() {
      this.$emit('syncContact', this.emailAccount.email);
    },
    hasBetaMessage(providerId) {
      return providerId !== this.emailAccountTypes.gmail.id && providerId !== this.emailAccountTypes.imap.id;
    },
    hasAccessToGrantAccess(providerId, featureId) {
      if (providerId === this.emailAccountTypes.gmail.id) {
        return true;
      }

      if (providerId === this.emailAccountTypes.outlook.id) {
        if (featureId === this.features.calendarSync.id) {
          return this.userHasFeatureFlag('outlook-calendar-sync');
        }

        if (featureId === this.features.emailSend.id) {
          return this.userHasFeatureFlag('outlook-email-send');
        }
      }

      if (providerId === this.emailAccountTypes.exchange.id) {
        if (featureId === this.features.calendarSync.id) {
          return this.userHasFeatureFlag('exchange-calendar-sync');
        }

        if (featureId === this.features.emailSend.id) {
          return this.userHasFeatureFlag('exchange-email-send');
        }
      }

      return true;
    },
  },
};
</script>
<style lang="scss" scoped>
.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 30px;
  height: 40px;
  width: 40px;
  float: left;

  svg {
    fill: #ffffff;
  }

  .icon {
    color: #ffffff;
    height: 22px;
    width: 22px;
  }
}

.provider-gmail {
  background-color: var(--v-error-darken1);
}

.provider-imap {
  background-color: var(--v-background-lighten3);
}

.provider-outlook {
  background-color: var(--v-primary-darken1);
}

.provider-exchange {
  background-color: var(--v-background-darken2);
}

.provider-default {
  background-color: var(--v-background-darken1);
}

.service-header {
  background-color: var(--v-background-lighten4);
  font-weight: bold;
}

.grant-access-button {
  color: #ffffff;
  border-radius: 5px;
  font-weight: bold;
  height: 20px;
}

.service-row {
  border-bottom: solid 1px var(--v-background-lighten4);
}
</style>
